import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { OAuthModule } from 'angular-oauth2-oidc';
import { AppErrorHandler } from './app-error.handler';
import { AccountEndpoint } from './services/account-endpoint.service';
import { AccountService } from './services/account.service';
import { ConfigurationService } from './services/configuration.service';
import { LocalStoreManager } from './services/local-store-manager.service';
import { OidcHelperService } from './services/oidc-helper.service';
import { NgbDateParserFormatter, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MainComponent } from './main.component';
import { HomeComponent } from './components/home/home.component';
import { LoginComponent } from './components/login/login.component';
import { HeaderComponent } from './components/layout/header/header.component';
import { SidebarComponent } from './components/layout/sidebar/sidebar.component';
import { ClickOutsideModule } from 'ng-click-outside';
import { EditorModule } from '@tinymce/tinymce-angular';

import { AuthHeadersInterceptorService } from './services/auth-headers-interceptor.service';
import { ConfirmModalComponent } from './shared/modals/confirm-modal/confirm-modal.component';
import { AlertComponent } from './shared/alert/alert.component';

import { NgSelectModule } from '@ng-select/ng-select';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';

import { MediaLibraryComponent } from './components/media-library/media-library.component';
import { NewFolderModalComponent } from './shared/modals/new-folder-modal/new-folder-modal.component';
import { EditAssetModalComponent } from './shared/modals/edit-asset-modal/edit-asset-modal.component';

import { UserNewComponent } from './components/user/user-new/user-new.component';
import { UserEditComponent } from './components/user/user-edit/user-edit.component';
import { UserListComponent } from './components/user/user-list/user-list.component';
import { MediaModalComponent } from './shared/modals/media-modal/media-modal.component';
import { BranchNewComponent } from './components/branch/branch-new/branch-new.component';
import { BranchEditComponent } from './components/branch/branch-edit/branch-edit.component';
import { BranchListComponent } from './components/branch/branch-list/branch-list.component';
import { LocationEditComponent } from './components/location/location-edit/location-edit.component';
import { LocationListComponent } from './components/location/location-list/location-list.component';
import { LocationNewComponent } from './components/location/location-new/location-new.component';
import { DigitOnlyDirective } from './directives/digit-only.directive';
import { SectionEditComponent } from './components/section/section-edit/section-edit.component';
import { SectionListComponent } from './components/section/section-list/section-list.component';
import { SectionNewComponent } from './components/section/section-new/section-new.component';
import { ProductEditComponent } from './components/product/product-edit/product-edit.component';
import { ProductListComponent } from './components/product/product-list/product-list.component';
import { ProductNewComponent } from './components/product/product-new/product-new.component';
import { HomePageComponent } from './components/home-page/home-page/home-page.component';
import { UserPasswordEditComponent } from './components/user/user-password-edit/user-password-edit.component';
import { CustomFormsModule } from 'ngx-custom-validators';
import { ProductAvailabilityComponent } from './components/management/product-availability/product-availability.component';
import { OrderListComponent } from './components/order/order-list/order-list.component';
import { ViewOrderModalComponent } from './shared/modals/view-order-modal/view-order-modal.component';
import { registerLocaleData } from '@angular/common';

import localeDeAt from '@angular/common/locales/ar-EG';
import { SurveyComponent } from './components/engagement/survey/survey.component';
import { ReviewComponent } from './components/engagement/review/review.component';
import { ContactComponent } from './components/engagement/contact/contact.component';

import { ConnectionServiceModule, ConnectionServiceOptions, ConnectionServiceOptionsToken } from 'ngx-connection-service';

registerLocaleData(localeDeAt);

import { NgxPrintModule } from 'ngx-print';
import { ContactViewModalComponent } from './shared/modals/contact-view-modal/contact-view-modal.component';
import { ProductReviewModalComponent } from './shared/modals/product-review-modal/product-review-modal.component';
import { EndOfDayReportComponent } from './components/reports/end-of-day-report/end-of-day-report.component';
import { NgbDateCustomParserFormatter } from './helpers/ngb-date-custom-parser-formatter';
import { DiscountPeriodComponent } from './components/offer/discount-period/discount-period.component';
import { VoucherListComponent } from './components/offer/voucher-code/voucher-list/voucher-list.component';
import { VoucherNewComponent } from './components/offer/voucher-code/voucher-new/voucher-new.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { SlickCarouselModule } from 'ngx-slick-carousel';
import { OfferComponent } from './components/offer/offer/offer.component';
import { ComplainListComponent } from './components/complain/complain-list/complain-list.component';
import { ComplainEditComponent } from './components/complain/complain-edit/complain-edit.component';
import { ComplainNewComponent } from './components/complain/complain-new/complain-new.component';

@NgModule({
    declarations: [
        AppComponent,
        MainComponent,
        HomeComponent,
        LoginComponent,
        HeaderComponent,
        SidebarComponent,
        ConfirmModalComponent,
        AlertComponent,
        MediaLibraryComponent,
        NewFolderModalComponent,
        EditAssetModalComponent,
        MediaModalComponent,
        UserNewComponent,
        UserEditComponent,
        UserListComponent,
        UserPasswordEditComponent,
        BranchNewComponent,
        BranchEditComponent,
        BranchListComponent,
        LocationEditComponent,
        LocationListComponent,
        LocationNewComponent,
        ComplainListComponent,
        ComplainEditComponent,
        ComplainNewComponent,
        DigitOnlyDirective,
        SectionListComponent,
        SectionNewComponent,
        SectionEditComponent,
        ProductNewComponent,
        ProductListComponent,
        ProductEditComponent,
        HomePageComponent,
        ProductAvailabilityComponent,
        OrderListComponent,
        ViewOrderModalComponent,
        ContactViewModalComponent,
        ProductReviewModalComponent,
        ContactComponent,
        ReviewComponent,
        SurveyComponent,
        EndOfDayReportComponent,
        DiscountPeriodComponent,
        VoucherListComponent,
        VoucherNewComponent,
        OfferComponent,
    ],
    entryComponents: [
        ConfirmModalComponent,
        NewFolderModalComponent,
        EditAssetModalComponent,
        MediaModalComponent,
        ViewOrderModalComponent,
        ContactViewModalComponent,
        ProductReviewModalComponent,
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        HttpClientModule,
        FormsModule,
        ReactiveFormsModule,
        AppRoutingModule,
        OAuthModule.forRoot(),
        NgbModule,
        ClickOutsideModule,
        EditorModule,
        NgSelectModule,
        NgxDatatableModule,
        CustomFormsModule,
        ConnectionServiceModule,
        NgxPrintModule,
        ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
        SlickCarouselModule,
    ],
    providers: [
        { provide: HTTP_INTERCEPTORS, useClass: AuthHeadersInterceptorService, multi: true },
        { provide: ErrorHandler, useClass: AppErrorHandler },
        {
            provide: ConnectionServiceOptionsToken,
            useValue: <ConnectionServiceOptions>{
                enableHeartbeat: true,
                heartbeatUrl: '/assets/data/ping.json',
                requestMethod: 'get',
                heartbeatInterval: 60000,
            },
        },
        { provide: NgbDateParserFormatter, useClass: NgbDateCustomParserFormatter },
        ConfigurationService,
        AccountService,
        AccountEndpoint,
        LocalStoreManager,
        OidcHelperService,
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
